// Finance > Vendor Cafe (Stephanie 2026-10-30). Bozzuto pays through its
// Vendor Cafe portal: each invoice is uploaded there (PDF + property name,
// invoice number, date and total typed in by hand) once every proof on the
// order is approved. This is the to-do list: Ready to upload / Waiting on
// proofs / Uploaded (last 60 days). Copy buttons for each field, the PDF,
// and the "Uploaded" tick. Data: GET /vendor-cafe/invoices.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { FilterTabs, TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { CopyValue, invoicePdfHref, vcAmount, vcDate, vcMoney, vcUploadedLine, type VendorCafeInvoice } from "@/components/vendor-cafe/shared";

type Bucket = VendorCafeInvoice["bucket"];

export function VendorCafePage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["vendor-cafe", "invoices"],
    queryFn: () => api<{ available: boolean; invoices: VendorCafeInvoice[] }>("/vendor-cafe/invoices"),
  });
  const [bucket, setBucket] = useState<Bucket>("ready");
  const [error, setError] = useState<string | null>(null);
  const tick = useMutation({
    mutationFn: (v: { id: string; uploaded: boolean }) =>
      api(`/invoices/${v.id}/vendor-cafe`, { method: "PATCH", body: JSON.stringify({ uploaded: v.uploaded }) }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["vendor-cafe"] });
    },
    onError: (e) => setError(apiErrorMessage(e, "We couldn't save that. Nothing was changed — try again.")),
  });
  const all = q.data?.invoices ?? [];
  const count = (b: Bucket) => all.filter((i) => i.bucket === b).length;
  const rows = all.filter((i) => i.bucket === bucket);
  if (bucket === "uploaded") rows.sort((a, b) => (b.uploadedAt ?? "").localeCompare(a.uploadedAt ?? ""));

  return (
    <div>
      <PageHeader
        eyebrow="Finance"
        title="Vendor Cafe"
        intro="Bozzuto invoices to upload to Vendor Cafe. Upload once every proof is approved: open the PDF, copy each detail across, then tick Uploaded."
      />
      {q.data && !q.data.available ? (
        <p className="rounded-md border border-rule bg-white px-5 py-6 text-sm text-ink">
          Vendor Cafe needs a one-time database step before it can be used. It's queued for Jake.
        </p>
      ) : (
        <>
          <div className="mb-4 border-b border-rule">
            <FilterTabs<Bucket>
              ariaLabel="Vendor Cafe invoices"
              value={bucket}
              onChange={setBucket}
              options={[
                { value: "ready", label: `Ready to upload (${count("ready")})` },
                { value: "waiting", label: `Waiting on proofs (${count("waiting")})` },
                { value: "uploaded", label: `Uploaded (${count("uploaded")})` },
              ]}
            />
          </div>
          {error && <p className="mb-3 text-sm text-rust-600">{error}</p>}
          {q.isLoading ? (
            <div className="h-24 animate-pulse rounded-md bg-white" aria-label="Loading" />
          ) : rows.length === 0 ? (
            <p className="rounded-md border border-rule bg-white px-5 py-6 text-center text-sm text-muted">
              {bucket === "ready" ? "Nothing to upload right now." : bucket === "waiting" ? "No invoices waiting on proofs." : "Nothing uploaded in the last 60 days."}
            </p>
          ) : (
            <div className={TABLE_WRAP}>
              <table className="w-full text-sm">
                <thead className={TABLE_HEAD}>
                  <tr>
                    <th className={TH}>Property name</th>
                    <th className={TH}>Invoice No.</th>
                    <th className={TH}>Invoice date</th>
                    <th className={`${TH} text-right`}>Invoice total</th>
                    <th className={TH}>{bucket === "uploaded" ? "Uploaded" : ""}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((i) => (
                    <tr key={i.id} className={TROW}>
                      <td className={TD}>
                        <CopyValue value={i.property} label="property name" />
                        {i.property !== i.clientName ? <span className="block text-xs text-muted">{i.clientName}</span> : null}
                      </td>
                      <td className={TD}>
                        <span className="inline-flex items-center gap-2">
                          <CopyValue value={String(i.invoiceNumber)} label="invoice number" />
                          <Link to={`/invoices/${i.id}`} className="text-xs text-muted hover:text-ink hover:underline">open</Link>
                        </span>
                      </td>
                      <td className={TD}><CopyValue value={vcDate(i.invoiceDate)} label="invoice date" /></td>
                      <td className={`${TD} text-right`}><CopyValue value={vcAmount(i.total)} label="invoice total" shown={vcMoney(i.total)} /></td>
                      <td className={TD}>
                        <span className="flex flex-wrap items-center justify-end gap-2">
                          <a href={invoicePdfHref(i.publicShareUuid)} target="_blank" rel="noopener noreferrer" className="inline-flex h-8 items-center rounded-md border border-rule bg-white px-3 text-xs font-medium text-ink hover:bg-cream-50">
                            PDF
                          </a>
                          {i.uploadedAt ? (
                            <>
                              <span className="text-xs text-muted">{vcUploadedLine(i)}</span>
                              <button type="button" disabled={tick.isPending} onClick={() => tick.mutate({ id: i.id, uploaded: false })} className="text-xs font-medium text-muted hover:text-ink">Undo</button>
                            </>
                          ) : (
                            <Button type="button" size="sm" variant={i.proofsApproved ? "primary" : "outline"} disabled={tick.isPending} onClick={() => tick.mutate({ id: i.id, uploaded: true })}>
                              Uploaded
                            </Button>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
